import type { API } from 'chessboard/api/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import type { MoveListComponent } from 'components/move-list/config/types';
import { Core } from 'chessboard/core/config/types';
import { createMoveList } from 'components/move-list';
import { defaultTranslations } from 'chessboard/model/move-list/translations';
import { MoveListPlugin } from 'chessboard/plugins/move-list/config/types';
import { positionChangeEvents } from 'chessboard/config/position-change-events';
import { is, isOneOf } from 'src/utils/app/model/event-handlers';
import { selectedNodeChangeEvents } from 'chessboard/config/selected-node-change-events';
import { Game } from 'chessboard/model/game/types';
import { forceNextFrame } from 'src/utils/force-next-frame/force-next-frame';
import { ArrowKeys } from '../types';
import shouldOpenVariationsPopover from './helpers/should-open-variations-popover';

// filter out position change events that only change the selected node, so we can handle those separately
const pgnPositionChangeEvents = positionChangeEvents.filter(
  (event) => !selectedNodeChangeEvents.includes(event),
);

/**
 * Create plugin that adds a move list to a given element.
 */
export function createMoveListPlugin({
  target,
  scrollContainer,
  hideTimestampText,
  displayTimeInClockFormat = true,
  showTimestamps = true,
  translations = defaultTranslations,
  loadLineOnCreate = true,
  customColorsEnabled = false,
}: MoveListPlugin.Options): Plugins.Model {
  let moveList: MoveListComponent.API;
  let destroyAPIExtensions: Function | undefined;

  return {
    api: createMoveListPluginAPI,
    create,
    destroy,
    match: [
      {
        condition: isOneOf([
          ...pgnPositionChangeEvents,
          Core.APIEvents.LineCommentUpdated,
          Core.APIEvents.LineUpdated,
          Core.APIEvents.AddMove,
          Core.InstanceEvents.Create,
          Core.InstanceEvents.ModeChanged,
        ]),
        handler: onLineUpdated,
      },
      {
        condition: isOneOf([...selectedNodeChangeEvents]),
        handler: onSelectedNodeChanged,
      },
      {
        condition: is(Core.OptionsEvents.Update),
        handler: onOptionsChange,
      },
      {
        condition: is(Core.APIEvents.UpdatePGNHeaders),
        handler: setGameData,
      },
      {
        condition: is(Core.APIEvents.NodeUpdated),
        handler: onNodeUpdated,
      },
    ],
    name: MoveListPlugin.Name,
  };

  /**
   * create the move list API
   */
  function createMoveListPluginAPI(context: API.Context): MoveListPlugin.API {
    return {
      setCommentsEditable: (enable: boolean) => {
        moveList.setCommentsEditable(enable);
      },
      enableNodeLimits: () => {
        moveList.enableNodeLimits();
        destroyAPIExtensions = context.api.extendAPI(MoveListPlugin.Name, {
          getDiagramNodeLimits: () => moveList.getNodeLimits,
        });
      },
      updateNodeLimits: (nodeLimits) => moveList.updateNodeLimits(nodeLimits),
      getMoveList: () => moveList,
      activate: () => {
        if (moveList.isActive()) {
          return;
        }

        moveList.setActiveStatus(true);

        const queue = moveList.getQueue();
        queue.clear({ persistPreserved: true });
        queue.add(loadLine, context.api, moveList);
        queue.syncFlush();
      },
      deactivate: () => {
        if (!moveList.isActive()) {
          return;
        }

        moveList.setActiveStatus(false);
        // Update move list immediately to ensure any queued updates that
        // depend on the game api are accurate before the move list gets
        // out of sync while deactivated.
        moveList.getQueue().syncFlush();
      },
    };
  }

  /**
   * Handle selected node change events
   */
  function onSelectedNodeChanged(_event: Core.Event, context: Core.Context) {
    const { line, move } = context.api.getPosition().ids;
    if (line === 0 && move === -1) {
      queueForNextFrame({}, moveList.selectMainLineStart);
      return;
    }

    queueForNextFrame({}, moveList.selectNode, context.api.getSelectedNode());
  }

  /**
   * Handle options change events
   */
  function onOptionsChange(event: Core.Event, context: Core.Context) {
    if (event.data?.enabled === true) {
      context.api['move-list']?.activate();
    } else if (event.data?.enabled === false) {
      context.api['move-list']?.deactivate();
    }

    queueForNextFrame({ persistOnClear: true }, () => {
      if ('moveListDisplayType' in event.data) {
        moveList.setTextType(event.data.moveListDisplayType);
      }

      if ('moveListClassificationStyle' in event.data) {
        moveList.setClassificationStyle(event.data.moveListClassificationStyle);
      }

      if ('moveListContextMenuEnabled' in event.data) {
        moveList.setContextMenuEnabled(event.data.moveListContextMenuEnabled);
      }
    });
  }

  /**
   * Handle setting timestamps
   */
  function onNodeUpdated(event: Core.Event, context: Core.Context) {
    const { api } = context;

    const node = event.data.node ?? event.data.changes?.node;
    if (!event.data.changes || !node) {
      return;
    }

    // reload layout for comment changes.
    // could optimize this further by only reloading when going from comment -> no comment or vice versa
    const hasCommentChanges = 'comment' in event.data.changes;
    if (hasCommentChanges) {
      queueForNextFrame({ clearQueue: true }, loadLine, api, moveList);

      // This is to avoid a race condition when editing a comment.
      // It is possible the user edits the comment again before the move list queue flushes... so let's just flush it now.
      moveList.getQueue().syncFlush();
      return;
    }

    const hasTimestampChanges = 'timestamp' in event.data.changes;
    const hasAnnotationChanges =
      'annotation' in event.data.changes || 'additionalAnnotation' in event.data.changes;
    const hasCustomColorChanges = 'customColor' in event.data.changes;
    if (hasTimestampChanges || hasAnnotationChanges || hasCustomColorChanges) {
      queueForNextFrame({}, moveList.refreshNode, node);
    }
  }

  /**
   * Handle updating the line
   */
  function onLineUpdated(event: Core.Event, context: Core.Context) {
    const { api } = context;
    const move = event.data?.move ?? event.data?.listMove;

    const result = api.getResult();
    const selectedNode = api.getSelectedNode();
    const isMoveEvent = event.type === Core.APIEvents.AddMove || event.type === Core.APIEvents.Move;

    if (move?.ids?.line === 0 && isMoveEvent && result === Game.Results.None) {
      // performance: shortcut recalculating the line layout when simply making a main line move
      queueForNextFrame({}, moveList.addMainLineNode, move, selectedNode);
      return;
    }

    queueForNextFrame({ clearQueue: true }, loadLine, api, moveList);
  }

  /**
   * Create the move list component.
   */
  function create(context: Core.Context) {
    const { api } = context;

    moveList = createMoveList({
      target,
      scrollContainer,
      game: api,
      hideTimestampText,
      displayTimeInClockFormat,
      showTimestamps,
      translations,
      customColorsEnabled,
    });

    if (loadLineOnCreate) {
      loadLine(api, moveList);
    }

    moveList.setActiveStatus(context.api.getOptions().enabled ?? true);

    (api[ArrowKeys.Plugin.Name] as ArrowKeys.API)?.setKeys({
      [ArrowKeys.Arrow.Right]: () => {
        if (moveList && shouldOpenVariationsPopover(api)) {
          moveList.openVariationsPopover();
          return;
        }

        api.moveForward();
      },
    });
  }

  /**
   * Set move list game data including result node and base time.
   */
  function setGameData(event: Core.Event, context: Core.Context) {
    const { api } = context;
    const result = api.getResult();
    const baseTime = api.timeControl.get()?.baseTime;

    queueForNextFrame({ persistOnClear: true }, () => {
      moveList.setResult(result);

      if ('TimeControl' in event.data) {
        moveList.setBaseTime(baseTime);
      }
    });
  }

  /**
   * Destroy the move list.
   */
  function destroy(context: Core.Context) {
    if (moveList) {
      moveList.destroy();
    }

    destroyAPIExtensions?.();
    (context?.api[ArrowKeys.Plugin.Name] as ArrowKeys.API)?.resetKeys();
  }

  /**
   * Queues a function to be called on the next frame. Provided options allow for queue manipulation.
   */
  function queueForNextFrame<T extends (...args: any[]) => void>(
    options: {
      /**
       * Clears the queue before adding the handler
       */
      clearQueue?: boolean;
      /**
       * Prevents the handler from being cleared if another handler clears the queue
       */
      persistOnClear?: boolean;
    },
    handler: T,
    ...params: Parameters<T>
  ): void {
    if (!moveList.isActive()) {
      return;
    }
    const queue = moveList.getQueue();

    if (options.clearQueue) {
      queue.clear({ persistPreserved: true });
    }

    queue.add({ preserved: options.persistOnClear ?? false }, handler, ...params);

    forceNextFrame(function flushMoveListQueue() {
      queue.syncFlush();
    });
  }
}

/**
 * Loads the line.
 */
function loadLine(api, moveList: MoveListComponent.API) {
  const lines = api.getRawLines();
  const result = api.getResult();
  const selectedNode = api.getSelectedNode();

  moveList.loadLine(lines, result, selectedNode);
}
